import { type RefObject, useLayoutEffect } from 'react';

type Params = {
  listRef: RefObject<HTMLElement | null>;
  moreRef: RefObject<HTMLElement | null>;
  indicatorRef: RefObject<HTMLElement | null>;
  isEnabled: boolean;
  activeValue: string | undefined;
  isMoreActive: boolean;
  childrenKey: string;
};

const ACTIVE_TRIGGER_SELECTOR =
  ':scope > [data-slot=tabs-trigger][data-state=active]';
const INDICATOR_INSET = 8;

export const useTabsIndicator = (params: Params) => {
  const {
    listRef,
    moreRef,
    indicatorRef,
    isEnabled,
    activeValue,
    isMoreActive,
    childrenKey,
  } = params;

  useLayoutEffect(() => {
    const list = listRef.current;
    const indicator = indicatorRef.current;
    if (!isEnabled || !list || !indicator) {
      return;
    }

    const updateIndicator = () => {
      const target = isMoreActive
        ? moreRef.current
        : list.querySelector<HTMLElement>(ACTIVE_TRIGGER_SELECTOR);
      if (!target) {
        indicator.style.opacity = '0';
        return;
      }
      indicator.style.opacity = '1';
      indicator.style.width = `${target.offsetWidth - INDICATOR_INSET * 2}px`;
      indicator.style.transform = `translateX(${target.offsetLeft + INDICATOR_INSET}px)`;
    };

    updateIndicator();
    const observer = new ResizeObserver(updateIndicator);
    observer.observe(list);
    const frame = requestAnimationFrame(() => {
      indicator.dataset.ready = 'true';
    });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [
    listRef,
    moreRef,
    indicatorRef,
    isEnabled,
    activeValue,
    isMoreActive,
    childrenKey,
  ]);
};
